import type { EnemyKind, EnemyShape } from '../types';
import { VISUALS } from './gameConfig';

// Single source of truth for enemy appearance per digit.
//
// Palettes are designed so every digit stays readable across all four themes:
// night themes reuse the base color as emissive glow, so nothing may be dark
// (the legacy palette's brown 1 and grey 0 vanished at night), and no two
// digits may share a hue family (legacy 0 and 9 were both grey).
export const PALETTES: Readonly<Record<string, Readonly<Record<number, number>>>> = {
  /** Warm, friendly, maximum-luminance — tuned for young kids & older adults. */
  candy: {
    0: 0xf7f7ff, // marshmallow
    1: 0xffb703, // butterscotch
    2: 0xff4d6d, // strawberry
    3: 0xff8500, // tangerine
    4: 0xffea00, // lemon
    5: 0x4cd964, // apple
    6: 0x3fa7ff, // sky
    7: 0xb15eff, // grape
    8: 0xff70c8, // bubblegum
    9: 0x40e0d0, // mint
  },
  /** Glow-forward; pairs with the cyan HUD. (If active, keep projectiles off cyan.) */
  neon: {
    0: 0xffffff,
    1: 0xffbf00,
    2: 0xff2965,
    3: 0xff6f00,
    4: 0xeeff00,
    5: 0x00ff66,
    6: 0x2979ff,
    7: 0xbf5fff,
    8: 0xff4fd8,
    9: 0x00e5ff,
  },
  /** Richer, best at dusk/night. */
  jewel: {
    0: 0xdde4f0,
    1: 0xf0a830,
    2: 0xe63950,
    3: 0xff6f3c,
    4: 0xffd166,
    5: 0x2ecc71,
    6: 0x3b82f6,
    7: 0x9b5de5,
    8: 0xff5fa2,
    9: 0x50e3c2,
  },
  /** The original prototype palette, kept for reference/nostalgia. */
  legacy: {
    0: 0x555555,
    1: 0x8b4513,
    2: 0xff0000,
    3: 0xff7f00,
    4: 0xffff00,
    5: 0x00ff00,
    6: 0x2266ff,
    7: 0x8a2be2,
    8: 0xff69b4,
    9: 0xcccccc,
  },
};

export const DEFAULT_COLOR = 0xff0000;

export function enemyColor(digit: number): number {
  const palette = PALETTES[VISUALS.palette] ?? PALETTES.candy;
  return palette[digit] ?? DEFAULT_COLOR;
}

/** Which solid each digit appears as. Shape encodes the number: the mapping
 *  follows the geometry where possible (tetrahedron→triangular faces→3,
 *  cube→square faces→4, dodecahedron→pentagonal faces→5, octahedron→8 faces,
 *  torus→looks like 0, cylinder→looks like 1). */
export const ENEMY_SHAPES: Readonly<Record<number, EnemyShape>> = {
  0: 'torus',
  1: 'cylinder',
  2: 'pizzaBox',
  3: 'tetrahedron',
  4: 'cube',
  5: 'dodecahedron',
  6: 'icosahedron',
  7: 'stellatedOctahedron',
  8: 'octahedron',
  9: 'stellatedDodecahedron',
};

export function enemyShape(digit: number): EnemyShape {
  return ENEMY_SHAPES[digit] ?? 'cube';
}

/** Visual variants an enemy may spawn as. Spawn picks one at random. */
export const ENEMY_KINDS: readonly EnemyKind[] = ['metal', 'glass'];
